import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Select,
  useToast,
} from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { CAP, useCatalogue, useOnboardAnalyzer } from "./api";
import type { AnalyzerProfile } from "./types";
import { PageHeader } from "./components/PageHeader";
import { Stepper } from "./components/Stepper";
import { QueryBoundary } from "./components/QueryBoundary";
import { disciplineLabel, PROTOCOLE_LABEL } from "./lib";

type StepId = "fabricant" | "modele" | "protocole" | "connexion";
const STEP_ORDER: StepId[] = ["fabricant", "modele", "protocole", "connexion"];

/**
 * OnboardingWizardPage — the showcase flow: fabricant → modèle → protocole → branchement, minimal
 * clicks over the ~75 analyzer profiles. The pick pre-fills protocol/transport/mode from the profile
 * (a known-family analyzer is pure config), so the only manual step is the internal code + the
 * IP/port (serial COM is set later on the device detail). Keyboard-first, FR/AR-RTL, 5-state DoD.
 */
export function OnboardingWizardPage() {
  const { t } = useT();
  const canConfigure = useCan(CAP.configure);
  const catalogueQuery = useCatalogue();

  return (
    <div>
      <PageHeader
        title={t("lis.onboarding.title")}
        subtitle={t("lis.onboarding.subtitle")}
      />
      <QueryBoundary q={catalogueQuery} loadingVariant="spinner">
        {(data) => <Wizard profiles={data.profiles} canConfigure={canConfigure} />}
      </QueryBoundary>
    </div>
  );
}

function Wizard({
  profiles,
  canConfigure,
}: {
  profiles: AnalyzerProfile[];
  canConfigure: boolean;
}) {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const onboard = useOnboardAnalyzer();

  const [stepIndex, setStepIndex] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [fabricant, setFabricant] = useState<string | null>(null);
  const [profile, setProfile] = useState<AnalyzerProfile | null>(null);

  // overridable connection fields (pre-filled from the profile)
  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [adresseIp, setAdresseIp] = useState("");
  const [port, setPort] = useState("");
  const [protocole, setProtocole] = useState<AnalyzerProfile["protocole"] | "">("");
  const [transport, setTransport] = useState<AnalyzerProfile["transport"] | "">("");

  const fabricants = useMemo(
    () => [...new Set(profiles.map((p) => p.fabricant))].sort((a, b) => a.localeCompare(b, "fr")),
    [profiles],
  );

  const goto = (i: number) => {
    setStepIndex(i);
    setMaxReached((m) => Math.max(m, i));
  };

  const pickFabricant = (f: string) => {
    if (f !== fabricant) {
      setFabricant(f);
      setProfile(null);
    }
    goto(1);
  };

  const pickProfile = (p: AnalyzerProfile) => {
    setProfile(p);
    setProtocole(p.protocole);
    setTransport(p.transport);
    if (!libelle) setLibelle(p.modele);
    goto(2);
  };

  const steps = STEP_ORDER.map((id) => ({ id, label: t(`lis.onboarding.step.${id}`) }));

  const codeValid = code.trim().length > 0;
  const canSubmit = canConfigure && !!profile && codeValid && !onboard.isPending;

  const submit = () => {
    if (!profile || !canSubmit) return;
    const overrides: Record<string, unknown> = {};
    if (protocole && protocole !== profile.protocole) overrides["protocole"] = protocole;
    if (transport && transport !== profile.transport) overrides["transport"] = transport;
    const portNum = port.trim() ? Number(port.trim()) : undefined;
    onboard.mutate(
      {
        profile_key: profile.key,
        code: code.trim(),
        ...(libelle.trim() ? { libelle: libelle.trim() } : {}),
        ...(adresseIp.trim() ? { adresse_ip: adresseIp.trim() } : {}),
        ...(portNum !== undefined && !Number.isNaN(portNum) ? { port: portNum } : {}),
        ...(Object.keys(overrides).length ? { overrides } : {}),
      },
      {
        onSuccess: () => {
          toast.success(t("lis.onboarding.success", { code: code.trim() }));
          navigate("/lis");
        },
        onError: (err) => toast.error(err.message, t("lis.onboarding.error")),
      },
    );
  };

  const stepId = STEP_ORDER[stepIndex];

  return (
    <div>
      <Stepper steps={steps} activeIndex={stepIndex} maxReached={maxReached} onJump={goto} />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: token.s4 }}>
        {stepId === "fabricant" && (
          <FabricantStep fabricants={fabricants} selected={fabricant} onPick={pickFabricant} />
        )}
        {stepId === "modele" && (
          <ModeleStep
            profiles={profiles}
            fabricant={fabricant}
            selectedKey={profile?.key ?? null}
            onPick={pickProfile}
          />
        )}
        {stepId === "protocole" && profile && (
          <ProtocoleStep
            profile={profile}
            protocole={protocole || profile.protocole}
            transport={transport || profile.transport}
            onProtocole={setProtocole}
            onTransport={setTransport}
          />
        )}
        {stepId === "connexion" && profile && (
          <ConnexionStep
            profile={profile}
            code={code}
            libelle={libelle}
            adresseIp={adresseIp}
            port={port}
            transport={transport || profile.transport}
            codeValid={codeValid}
            onCode={setCode}
            onLibelle={setLibelle}
            onIp={setAdresseIp}
            onPort={setPort}
          />
        )}
      </div>

      {/* Wizard nav */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: token.s2,
          marginBlockStart: token.s5,
        }}
      >
        <Button
          variant="ghost"
          onClick={() => goto(Math.max(0, stepIndex - 1))}
          disabled={stepIndex === 0}
        >
          ‹ {t("lis.action.previous")}
        </Button>
        {stepId !== "connexion" ? (
          <Button
            variant="primary"
            onClick={() => goto(Math.min(STEP_ORDER.length - 1, stepIndex + 1))}
            disabled={
              (stepId === "fabricant" && !fabricant) ||
              (stepId === "modele" && !profile) ||
              (stepId === "protocole" && !profile)
            }
          >
            {t("lis.action.next")} ›
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={submit}
            loading={onboard.isPending}
            disabled={!canSubmit}
            title={!canConfigure ? CAP.configure : undefined}
          >
            {t("lis.onboarding.submit")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Step 1 — fabricant ──────────────────────────────────────────────────────────
function FabricantStep({
  fabricants,
  selected,
  onPick,
}: {
  fabricants: string[];
  selected: string | null;
  onPick: (f: string) => void;
}) {
  const { t } = useT();
  return (
    <Card title={t("lis.onboarding.step.fabricant")}>
      <p style={{ marginBlockStart: 0, color: token.slate }}>
        {t("lis.onboarding.step.fabricant.hint")}
      </p>
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: token.s2,
        }}
      >
        {fabricants.map((f) => {
          const active = f === selected;
          return (
            <li key={f}>
              <button
                type="button"
                onClick={() => onPick(f)}
                aria-pressed={active}
                style={{
                  inlineSize: "100%",
                  textAlign: "start",
                  padding: token.s3,
                  border: `1px solid ${active ? token.azure : token.line}`,
                  borderRadius: token.rMd,
                  background: active ? token.azureSoft : token.white,
                  color: token.ink,
                  font: "inherit",
                  cursor: "pointer",
                  minBlockSize: token.minTouch,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: token.s2,
                }}
              >
                <span style={{ fontWeight: 500 }}>{f}</span>
                <span aria-hidden style={{ color: token.steel }}>
                  ›
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ── Step 2 — modèle ──────────────────────────────────────────────────────────────
function ModeleStep({
  profiles,
  fabricant,
  selectedKey,
  onPick,
}: {
  profiles: AnalyzerProfile[];
  fabricant: string | null;
  selectedKey: string | null;
  onPick: (p: AnalyzerProfile) => void;
}) {
  const { t } = useT();
  const [discipline, setDiscipline] = useState("");
  const [q, setQ] = useState("");

  const forFabricant = useMemo(
    () => (fabricant ? profiles.filter((p) => p.fabricant === fabricant) : []),
    [profiles, fabricant],
  );
  const disciplines = useMemo(
    () => [...new Set(forFabricant.map((p) => p.discipline))],
    [forFabricant],
  );
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return forFabricant.filter(
      (p) =>
        (!discipline || p.discipline === discipline) &&
        (!s || `${p.modele} ${p.key}`.toLowerCase().includes(s)),
    );
  }, [forFabricant, discipline, q]);

  if (!fabricant) {
    return (
      <Card title={t("lis.onboarding.step.modele")}>
        <EmptyState glyph="▢" title={t("lis.onboarding.pickFabricant")} />
      </Card>
    );
  }

  return (
    <Card
      title={t("lis.onboarding.step.modele")}
      actions={<Badge tone="neutral">{fabricant}</Badge>}
    >
      <p style={{ marginBlockStart: 0, color: token.slate }}>
        {t("lis.onboarding.step.modele.hint", { n: forFabricant.length })}
      </p>
      <div
        style={{
          display: "flex",
          gap: token.s2,
          marginBlockEnd: token.s3,
          flexWrap: "wrap",
        }}
      >
        <Input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("lis.action.search")}
          aria-label={t("lis.action.search")}
          style={{ flex: "2 1 220px" }}
          autoFocus
        />
        <Select
          value={discipline}
          onChange={(e) => setDiscipline(e.target.value)}
          aria-label={t("lis.onboarding.disciplineFilter")}
          style={{ flex: "1 1 160px" }}
        >
          <option value="">{t("lis.onboarding.disciplineAll")}</option>
          {disciplines.map((d) => (
            <option key={d} value={d}>
              {disciplineLabel(d)}
            </option>
          ))}
        </Select>
      </div>

      {list.length === 0 ? (
        <EmptyState glyph="▢" title={t("lis.onboarding.noProfiles")} />
      ) : (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: token.s2,
          }}
        >
          {list.map((p) => {
            const active = p.key === selectedKey;
            return (
              <li key={p.key}>
                <button
                  type="button"
                  onClick={() => onPick(p)}
                  aria-pressed={active}
                  style={{
                    inlineSize: "100%",
                    textAlign: "start",
                    padding: token.s3,
                    border: `1px solid ${active ? token.azure : token.line}`,
                    borderRadius: token.rMd,
                    background: active ? token.azureSoft : token.white,
                    color: token.ink,
                    font: "inherit",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    gap: token.s1,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{p.modele}</span>
                  <span
                    style={{
                      display: "flex",
                      gap: token.s1,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <Badge tone="neutral">{disciplineLabel(p.discipline)}</Badge>
                    <Badge tone="info">{PROTOCOLE_LABEL[p.protocole]}</Badge>
                    <Badge tone={p.confidence === "verified" ? "success" : "warn"}>
                      {t(`lis.onboarding.confidence.${p.confidence}`)}
                    </Badge>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ── Step 3 — protocole / transport ───────────────────────────────────────────────
function ProtocoleStep({
  profile,
  protocole,
  transport,
  onProtocole,
  onTransport,
}: {
  profile: AnalyzerProfile;
  protocole: AnalyzerProfile["protocole"];
  transport: AnalyzerProfile["transport"];
  onProtocole: (p: AnalyzerProfile["protocole"]) => void;
  onTransport: (tr: AnalyzerProfile["transport"]) => void;
}) {
  const { t } = useT();
  return (
    <Card
      title={t("lis.onboarding.step.protocole")}
      actions={<Badge tone="info">{profile.modele}</Badge>}
    >
      <p style={{ marginBlockStart: 0, color: token.slate }}>
        {t("lis.onboarding.step.protocole.hint")}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: token.s3,
        }}
      >
        <FormField label={t("lis.config.protocole")}>
          {(p) => (
            <Select
              {...p}
              value={protocole}
              onChange={(e) => onProtocole(e.target.value as AnalyzerProfile["protocole"])}
              options={(["astm-e1394", "hl7v2-oru", "poct1a"] as const).map((v) => ({
                value: v,
                label: PROTOCOLE_LABEL[v],
              }))}
            />
          )}
        </FormField>
        <FormField label={t("lis.config.transport")}>
          {(p) => (
            <Select
              {...p}
              value={transport}
              onChange={(e) => onTransport(e.target.value as AnalyzerProfile["transport"])}
              options={(["serie", "tcp"] as const).map((v) => ({
                value: v,
                label: t(`lis.transport.${v}`),
              }))}
            />
          )}
        </FormField>
        <FormField label={t("lis.config.sens")}>
          {(p) => (
            <Input
              {...p}
              readOnly
              value={t(`lis.sens.${profile.sens}`)}
              style={{ background: token.surface }}
            />
          )}
        </FormField>
        <FormField label={t("lis.config.mode")}>
          {(p) => (
            <Input
              {...p}
              readOnly
              value={t(`lis.mode.${profile.mode_interaction}`)}
              style={{ background: token.surface }}
            />
          )}
        </FormField>
      </div>

      <section
        style={{
          marginBlockStart: token.s4,
          padding: token.s3,
          border: `1px solid ${token.lineSoft}`,
          borderRadius: token.rMd,
          background: token.surface,
        }}
      >
        <h3 style={{ fontSize: token.fsLabel, color: token.graphite, marginBlockEnd: token.s1 }}>
          {t("lis.onboarding.notes")}
        </h3>
        <p style={{ margin: 0, fontSize: token.fsBody, color: token.slate, lineHeight: 1.6 }}>
          {profile.dialect_notes}
        </p>
      </section>
    </Card>
  );
}

// ── Step 4 — branchement ─────────────────────────────────────────────────────────
function ConnexionStep({
  profile,
  code,
  libelle,
  adresseIp,
  port,
  transport,
  codeValid,
  onCode,
  onLibelle,
  onIp,
  onPort,
}: {
  profile: AnalyzerProfile;
  code: string;
  libelle: string;
  adresseIp: string;
  port: string;
  transport: AnalyzerProfile["transport"];
  codeValid: boolean;
  onCode: (v: string) => void;
  onLibelle: (v: string) => void;
  onIp: (v: string) => void;
  onPort: (v: string) => void;
}) {
  const { t } = useT();
  const codeRef = useRef<HTMLInputElement>(null);
  const isTcp = transport === "tcp";
  const touched = useRef(false);

  return (
    <Card title={t("lis.onboarding.step.connexion")}>
      <p style={{ marginBlockStart: 0, color: token.slate }}>
        {t("lis.onboarding.step.connexion.hint")}
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: token.s3,
        }}
      >
        <FormField
          label={t("lis.onboarding.field.code")}
          required
          hint={t("lis.onboarding.field.code.hint")}
          {...(!codeValid && touched.current ? { error: t("lis.onboarding.field.code") } : {})}
        >
          {(p) => (
            <Input
              {...p}
              ref={codeRef}
              mono
              value={code}
              onChange={(e) => {
                touched.current = true;
                onCode(e.target.value);
              }}
              autoFocus
              placeholder="BIO-1"
            />
          )}
        </FormField>
        <FormField label={t("lis.onboarding.field.libelle")}>
          {(p) => (
            <Input {...p} value={libelle} onChange={(e) => onLibelle(e.target.value)} placeholder={profile.modele} />
          )}
        </FormField>
        {isTcp ? (
          <>
            <FormField label={t("lis.onboarding.field.ip")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  inputMode="numeric"
                  value={adresseIp}
                  onChange={(e) => onIp(e.target.value)}
                  placeholder="192.168.1.50"
                />
              )}
            </FormField>
            <FormField label={t("lis.onboarding.field.port")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  inputMode="numeric"
                  value={port}
                  onChange={(e) => onPort(e.target.value)}
                  placeholder="9100"
                />
              )}
            </FormField>
          </>
        ) : (
          <p
            style={{
              gridColumn: "1 / -1",
              margin: 0,
              padding: token.s3,
              border: `1px solid ${token.lineSoft}`,
              borderRadius: token.rMd,
              background: token.surface,
              color: token.slate,
              fontSize: token.fsBody,
            }}
          >
            {t("lis.onboarding.serial.note")}
          </p>
        )}
      </div>

      {/* Recap */}
      <section
        style={{
          marginBlockStart: token.s4,
          padding: token.s3,
          border: `1px solid ${token.line}`,
          borderRadius: token.rMd,
        }}
      >
        <h3 style={{ fontSize: token.fsLabel, color: token.graphite, marginBlockEnd: token.s2 }}>
          {t("lis.onboarding.summary")}
        </h3>
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: `${token.s1} ${token.s4}`,
            margin: 0,
          }}
        >
          <dt style={{ color: token.slate }}>{t("lis.onboarding.step.fabricant")}</dt>
          <dd style={{ margin: 0 }}>{profile.fabricant}</dd>
          <dt style={{ color: token.slate }}>{t("lis.onboarding.step.modele")}</dt>
          <dd style={{ margin: 0 }}>{profile.modele}</dd>
          <dt style={{ color: token.slate }}>{t("lis.config.protocole")}</dt>
          <dd style={{ margin: 0 }}>{PROTOCOLE_LABEL[profile.protocole]}</dd>
          <dt style={{ color: token.slate }}>{t("lis.config.transport")}</dt>
          <dd style={{ margin: 0 }}>{t(`lis.transport.${transport}`)}</dd>
        </dl>
      </section>
    </Card>
  );
}
